import { html, LitElement, PropertyValues } from "lit";
import { queryAssignedElements, customElement, state } from "lit/decorators.js";
import style from './tab-layout.scss';
import { map } from "lit/directives/map.js";

interface TabInfo { name: string, disabled: boolean }

/**
 * A tab layout component that allows switching between different content panels.
 * @fires 'tab-layout:change' when the selected tab changes, with detail { selectedTabIndex: number }
 */
@customElement('tab-layout')
export class TabLayout extends LitElement {
    static override styles = style;


    @queryAssignedElements() private tabElements!: HTMLElement[];

    @state() private tabInfos: TabInfo[] = [];

    @state() private selectedTabId = 0;


    private _findTabIdx(tabId: number | string): Optional<number> {
        let id: number;
        if (typeof tabId == 'string') {
            id = this.tabElements.findIndex(el => el.id == tabId);
        } else {
            id = tabId;
        }
        if (id < 0 || id >= this.tabInfos.length) return null;
        return id;
    }

    /**
     * Updates the label of a tab.
     *
     * @param tabId - index of the tab (starting from 0) or the `id` of the child element.
     * @param newLabel - new label string or a function transforming the previous label.
     *
     * If the tab exists, its label is updated both in the internal state and in the
     * corresponding child element's `data-label` attribute. The component does not
     * observe changes to `data-label`, so this method must be used for dynamic updates.
     */
    updateLabel(tabId: number | string, newLabel: string | ((lastName: string) => string)) {
        const id = this._findTabIdx(tabId);
        if (id === null) return;

        let label: string;
        if (typeof newLabel == 'string') {
            label = newLabel;
        } else {
            label = newLabel(this.tabInfos[id].name);
        }
        this.tabInfos[id].name = label;
        this.tabElements[id].dataset.label = label;
        this.tabInfos = Array(...this.tabInfos); // in order to request update
    }

    /**
     * Updates the disabled state of a tab.
     *
     * @param tabId - index of the tab (starting from 0) or the `id` of the child element.
     * @param newValue - `true` to disable the tab, `false` to enable it.
     *
     * Disabled tabs cannot be activated and are skipped during keyboard navigation.
     * The component requires at least one non-disabled tab; disabling all tabs will
     * result in invalid component state and must be avoided by the caller.
     */
    updateDisabled(tabId: number | string, newValue: boolean) {
        const id = this._findTabIdx(tabId);
        if (id === null) return;

        this.tabInfos[id].disabled = newValue;
        this.tabElements[id].dataset.disabled = String(newValue);
        this.tabInfos = Array(...this.tabInfos); // in order to request update
    }


    protected override render() {
        return html`
            <div id="tablist" role="tablist" tabindex=0 @click=${this._onClick} @keydown=${this._onKeydown}>
                ${map(this.tabInfos, (tab, index) => html`
                    <span role="tab" data-index=${index}
                        ?data-selected=${this.selectedTabId == index} aria-selected=${this.selectedTabId == index}
                        aria-disabled=${tab.disabled}
                        aria-controls="tablist">${tab.name}</span>`
                )}
            </div>
            <div id="tabs">
                <slot @slotchange=${this._onSlotChange}></slot>
            </div>
        `;
    }


    private _onSlotChange() {
        this.tabElements.forEach(el => el.role = 'tabpanel');
        this.tabInfos = this.tabElements.map(el => ({
            name: el.dataset.label ?? '',
            disabled: el.dataset.disabled === "true"
        }));
        this.selectedTabId = this.tabInfos.findIndex(tabInfo => !tabInfo.disabled);
    }

    private _onKeydown(e: KeyboardEvent) {
        /* None of the required keyboard interaction uses special keys.
         * We should not block any extra browser shortcuts. */
        if (e.altKey || e.ctrlKey || e.shiftKey || e.metaKey) return;

        if (e.key == 'ArrowLeft' || e.key == 'ArrowUp') {
            this.selectedTabId = this.selectedTabId > 0 ? this.selectedTabId - 1 : this.tabInfos.length - 1;
        } else if (e.key == 'ArrowRight' || e.key == 'ArrowDown') {
            this.selectedTabId = this.selectedTabId < this.tabInfos.length - 1 ? this.selectedTabId + 1 : 0;
        } else if (e.key == 'Home') {
            this.selectedTabId = this.tabInfos.findIndex(tabInfo => !tabInfo.disabled);
        } else if (e.key == 'End') {
            this.selectedTabId = this.tabInfos.findLastIndex(tabInfo => !tabInfo.disabled);
        } else if (e.key == ' ' || e.key == 'Enter') {
            /* Preventing default because these keys would activate tab
             * if it wasn't activated automatically on focus. */
        } else return;
        if (this.tabInfos[this.selectedTabId].disabled &&
            ['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown'].includes(e.key)) {
            /* Keep moving in the same direction until a non-disabled tab is reached */
            this._onKeydown(e);
        }
        e.preventDefault();
    }

    private _onClick(e: PointerEvent) {
        const newTabId = Number((e.target as HTMLElement).dataset.index);
        const isDisabled = this.tabInfos[newTabId].disabled;
        if (0 <= newTabId && newTabId < this.tabInfos.length && !isDisabled)
            this.selectedTabId = newTabId;
    }


    override updated(changedProperties: PropertyValues) {
        if (changedProperties.has('selectedTabId') || changedProperties.has('tabInfos')) {
            this.tabElements.forEach((el, index) => el.style.display = index != this.selectedTabId ? 'none' : '');
        }
        if (changedProperties.has('selectedTabId')) {
            this.dispatchEvent(new CustomEvent('tab-layout:change', {
                detail: {
                    selectedTabIndex: this.selectedTabId
                }
            }));
        }
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'tab-layout': TabLayout,
    }
    interface HTMLElementEventMap {
        'tab-layout:change': CustomEvent<{ selectedTabIndex: number }>;
    }
}
